import { useRef, useEffect } from "react";
import Box from "@amzn/meridian/esm/components/box";
import Column from "@amzn/meridian/esm/components/column";
import ActionItemCard from "./ActionItemCard";
import ActionItemEditCard from "./ActionItemEditCard";
import AddButton from "../AddButton";
import EmptyState from "../EmptyState";
import Loading from "../../../components/Loading";
import ErrorState from "../ErrorState";
import { tabBodyClass, tabScrollClass } from "../insights.styles";
import { scrollToBottom } from "../insights.utils";
import { useActionItems, useActionItemActions } from "./actionItems.hooks";
import { ADD_ACTION_ITEM_LABEL, EMPTY_ACTION_ITEMS_MESSAGE } from "./actionItems.constants";
import { sortByDetectedAtDesc } from "./actionItems.utils";
import noAction from "../../../images/no_action.svg";
import { useSession } from "../../../context/SessionContext";

/**
 * ActionItems - Displays real-time action items detected during an active meeting session.
 *
 * Primary mode: receives items via WebSocket push through insightStore.
 * Fallback mode: polls /action-items every 5 seconds when WebSocket is unavailable.
 * Always does a one-time fetch on mount to establish initial state.
 *
 * Data comes from useActionItems and add/edit/delete state from useActionItemActions (see it for
 * the sticky-form / transient-delete rules). Spacing is expressed with container spacingInset /
 * Column spacing (no hard-coded spacer elements). While adding, the new-item form appears at the
 * bottom of the list and the "Add action item" button is hidden.
 */
const ActionItems = ({ insightStore }) => {
  const { meetingUuid } = useSession();
  const { items: actionItems, isLoading, error } = useActionItems(meetingUuid, insightStore);
  const { add, edit, del, highlightId } = useActionItemActions({ meetingUuid, insightStore });
  const listScrollRef = useRef(null);

  // The add form renders at the bottom of the list; reveal it when it opens. Refocusing the field
  // re-reveals it, which is the desired behavior.
  const revealAddForm = () => scrollToBottom(listScrollRef);
  const addCard = (
    <ActionItemEditCard onSave={add.save} onCancel={add.cancel} onContentFocus={revealAddForm} />
  );

  // New items prepend to the top; scroll the list back up on each add (highlightId changes per
  // add) so the freshly-added row is visible even if the user was scrolled down.
  useEffect(() => {
    if (highlightId && listScrollRef.current) {
      listScrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [highlightId]);

  // Bounded height so the tab never expands. Only the LIST scrolls (flex: 0 1 auto + its own
  // overflow): when the list is short it hugs its content so the "Add action item" button sits
  // directly below the last item; when the list is long it caps and scrolls internally while the
  // button stays just below it — always visible without scrolling to the bottom.
  return isLoading ? (
    <Loading />
  ) : error ? (
    <ErrorState message={error} />
  ) : actionItems.length === 0 && !add.open ? (
    <EmptyState
      image={noAction}
      message={EMPTY_ACTION_ITEMS_MESSAGE}
      action={<AddButton label={ADD_ACTION_ITEM_LABEL} variant="primary" onClick={add.start} />}
    />
  ) : (
    <div className={tabBodyClass}>
      <div ref={listScrollRef} className={tabScrollClass}>
        <Box spacingInset="350 400 none 450">
          <Column spacing="300">
            {sortByDetectedAtDesc(actionItems).map((item) =>
              edit.ids.has(item.itemId) ? (
                <ActionItemEditCard
                  key={item.itemId}
                  item={item}
                  onSave={(fields) => edit.save(item.itemId, fields)}
                  onCancel={() => edit.cancel(item.itemId)}
                />
              ) : (
                <ActionItemCard
                  key={item.itemId}
                  item={item}
                  highlighted={highlightId === item.itemId}
                  onEdit={() => edit.start(item)}
                  onDelete={() => del.start(item)}
                  confirming={del.pendingId === item.itemId}
                  onConfirmDelete={() => del.confirm(item)}
                  onCancelDelete={del.cancel}
                />
              )
            )}
            {add.open && <Box spacingInset="none none 350 none">{addCard}</Box>}
          </Column>
        </Box>
      </div>
      {!add.open && (
        <Box spacingInset="300 none 350 450">
          <AddButton label={ADD_ACTION_ITEM_LABEL} onClick={add.start} />
        </Box>
      )}
    </div>
  );
};

export default ActionItems;
